/**
 * Per-board coverage classification for the manage-rail "Protected
 * Boards" tab.
 *
 * The cover card sizes the weekly cap from EVERY protectable tile the
 * holder owns, but a tile is only genuinely covered when its UGM is in
 * the signed delegation's `protectedUgms`. The gap between those two
 * sets is exactly what confused the `0x908E…332E` report ("it says 35
 * tiles but only 1 board is covered"). This helper makes the gap
 * legible: it walks the holder's boards and tags each one covered or
 * not, and — when not — WHY, in a single non-technical reason code the
 * UI renders as a hover/tap tooltip.
 *
 * Pure module (no React, no env reads) so the classification is unit-
 * testable without rendering the rail.
 */

import type { Address } from 'viem';
import type { CoinSeatsData } from '@/components/seats/coin-seats-card';
import type { ProtectionUgmRow } from './budget';
import type { DelegationSigningPath } from '@/lib/wallet-capabilities';

/**
 * Why a board the holder owns tiles on isn't covered:
 *   - `metamask-old-board` — it's a 7710-only (V2.1) board and the
 *     wallet is on the MetaMask 7715 rail, which can't redeem against
 *     it. Remediation: switch to a 7710 wallet. Checked FIRST so a
 *     MetaMask holder never sees "re-enable" for a board re-enabling
 *     from MetaMask can never cover.
 *   - `not-onboarded` — the board's UGM isn't in the protocol allowlist
 *     yet. Remediation: none (auto-covers once onboarded).
 *   - `needs-reenable` — the board IS allowlisted now, but the holder's
 *     active delegation was signed before it was added, so a re-sign is
 *     needed to extend coverage to it.
 */
export type BoardCoverageReason =
  | 'metamask-old-board'
  | 'not-onboarded'
  | 'needs-reenable';

export interface BoardCoverageRow {
  board: CoinSeatsData;
  /** Lower-cased UGM the board belongs to. */
  ugmKey: string;
  /** True when the board's UGM is in the signed `protectedUgms`. */
  covered: boolean;
  /** Populated iff `covered === false`; null otherwise. */
  reason: BoardCoverageReason | null;
}

/**
 * Classify each of the holder's boards as covered / not-covered (+why).
 * Covered boards sort first; original heaviest-first row order is
 * preserved within each group (Array.prototype.sort is stable).
 *
 * `liveAllowedUgms` is the CURRENT env allowlist (not the stored one on
 * the delegation), so a board onboarded after the holder signed reads
 * as `needs-reenable` ("re-enable to cover this") rather than the
 * misleading `not-onboarded` ("we haven't added it yet").
 */
export function buildBoardCoverage(args: {
  ugmRows: ProtectionUgmRow[];
  protectedUgms: ReadonlyArray<string | Address>;
  liveAllowedUgms: ReadonlySet<string>;
  v21UgmsLower: ReadonlySet<string>;
  signingPath: DelegationSigningPath;
}): BoardCoverageRow[] {
  const { ugmRows, protectedUgms, liveAllowedUgms, v21UgmsLower, signingPath } = args;
  const protectedSet = new Set(
    protectedUgms.map((u) => (u as string).toLowerCase()),
  );

  const rows: BoardCoverageRow[] = [];
  for (const r of ugmRows) {
    const ugmKey = r.ugm.toLowerCase();
    const covered = protectedSet.has(ugmKey);
    let reason: BoardCoverageReason | null = null;
    if (!covered) {
      if (signingPath === '7715-grant' && v21UgmsLower.has(ugmKey)) {
        reason = 'metamask-old-board';
      } else if (!liveAllowedUgms.has(ugmKey)) {
        reason = 'not-onboarded';
      } else {
        reason = 'needs-reenable';
      }
    }
    for (const board of r.boards) rows.push({ board, ugmKey, covered, reason });
  }

  return rows.sort((a, b) => Number(b.covered) - Number(a.covered));
}

/** Brief, non-technical tooltip copy for an un-covered board. */
export function boardCoverageReasonCopy(reason: BoardCoverageReason): string {
  switch (reason) {
    case 'metamask-old-board':
      // Honest: a 7715 holder IS a MetaMask Smart Account, so their EOA
      // is bound — switching to Rabby/Coinbase/Frame fails (the address
      // is a smart account). This older (V2.1) board simply can't be
      // auto-protected from MetaMask; don't promise a wallet swap that
      // won't work.
      return "This older board can’t be auto-protected from a MetaMask smart account yet.";
    case 'not-onboarded':
      return "Not set up for protection yet — it'll be covered automatically once it's added.";
    case 'needs-reenable':
      return 'Turn protection off and back on to start covering this board.';
  }
}
